// Dashboard home widget — "Time Saved" by Jarvis on email. Three windows
// stacked: week / month / year. The year number is the retention hook —
// surfaced biggest.

import { useQuery } from '@tanstack/react-query'
import { Clock } from 'lucide-react'
import { JarvisCard } from '@/components/JarvisCard'
import { apiClient, unwrap } from '@/lib/apiClient'
import { HAS_AUTH } from '@/lib/auth'
import type { TimeSavingsAggregate } from '@/types'

function hoursLabel(totalMinutes: number): string {
  if (totalMinutes === 0) return '0 hrs'
  const hrs = totalMinutes / 60
  if (hrs >= 10) return `${Math.round(hrs)} hrs`
  return `${hrs.toFixed(1)} hrs`
}

export function TimeSavedWidget() {
  const week = useQuery<TimeSavingsAggregate>({
    queryKey: ['email-time-savings', 'week'],
    queryFn: () =>
      unwrap<TimeSavingsAggregate>(
        apiClient.get('/email/time-savings', { params: { period: 'week' } }),
      ),
    enabled: HAS_AUTH,
    retry: false,
    refetchInterval: 300_000,
  })
  const month = useQuery<TimeSavingsAggregate>({
    queryKey: ['email-time-savings', 'month'],
    queryFn: () =>
      unwrap<TimeSavingsAggregate>(
        apiClient.get('/email/time-savings', { params: { period: 'month' } }),
      ),
    enabled: HAS_AUTH,
    retry: false,
    refetchInterval: 300_000,
  })
  const year = useQuery<TimeSavingsAggregate>({
    queryKey: ['email-time-savings', 'year'],
    queryFn: () =>
      unwrap<TimeSavingsAggregate>(
        apiClient.get('/email/time-savings', { params: { period: 'year' } }),
      ),
    enabled: HAS_AUTH,
    retry: false,
    refetchInterval: 300_000,
  })

  const yearMinutes = year.data?.totalMinutes ?? 0

  // Hide the widget entirely when nothing has been saved yet — keeps the
  // dashboard tidy for users who haven't connected Gmail.
  if (
    !week.data &&
    !month.data &&
    !year.data &&
    !week.isLoading &&
    !month.isLoading &&
    !year.isLoading
  ) {
    return null
  }
  if (yearMinutes === 0 && !week.isLoading) {
    return null
  }

  return (
    <JarvisCard className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-jarvis-muted">
          Time Saved on Email
        </span>
        <Clock size={16} className="text-jarvis-muted" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Cell label="This week" value={hoursLabel(week.data?.totalMinutes ?? 0)} />
        <Cell label="This month" value={hoursLabel(month.data?.totalMinutes ?? 0)} />
        <Cell
          label="This year"
          value={hoursLabel(yearMinutes)}
          emphasized
        />
      </div>
    </JarvisCard>
  )
}

function Cell({
  label,
  value,
  emphasized,
}: {
  label: string
  value: string
  emphasized?: boolean
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-jarvis-muted mb-1">{label}</div>
      <div
        className={
          emphasized
            ? 'text-2xl font-semibold tabular text-jarvis-blue tracking-tight'
            : 'text-xl font-semibold tabular tracking-tight'
        }
      >
        {value}
      </div>
    </div>
  )
}
